import React, { useEffect, useState } from "react";

import {
  Box,
  Checkbox,
  Grid,
  Hidden,
  Slider,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { startCase } from "lodash";

import {
  DEFAULT_FROM,
  DEFAULT_TO,
  MAX_SLIDER_TIME,
  MIN_SLIDER_TIME,
  SLIDER_STEPS,
} from "../../constants";
import { DayHoursProps, SliderMark } from "../../types";
import { convertMinsSinceMidnightTo24HourTime } from "../../utils";

const DEFAULT_HOURS = [DEFAULT_FROM, DEFAULT_TO];

const useStyles = makeStyles(theme => ({
  row: {
    height: 72,
  },
  slides: {
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 12,
      paddingRight: 12,
    },
  },
}));

const sliderMarks: SliderMark[] = [];
for (let mins = MIN_SLIDER_TIME; mins <= MAX_SLIDER_TIME; mins++) {
  if (mins % 60 === 0) {
    sliderMarks.push({ value: mins });
  }
}

const formatTime = (mins: number) =>
  convertMinsSinceMidnightTo24HourTime(mins, {
    format: true,
  });

const DayHours: React.FC<DayHoursProps> = ({
  day,
  hours,
  toggleAvailability,
  handleSliderChange,
  disabled = false,
}) => {
  const classes = useStyles();
  const labelId = `select-${day}`;
  const [value, setValue] = useState<number[]>(hours || DEFAULT_HOURS);
  const isSelected = Boolean(hours);
  useEffect(() => {
    if (hours) {
      setValue(hours);
    }
  }, [hours]);
  return (
    <TableRow
      role="checkbox"
      aria-checked={isSelected}
      className={classes.row}
      data-test={`working-hours-row-${day}`}
    >
      <TableCell padding="checkbox" colSpan={3}>
        <Grid container>
          <Grid item xs={6} sm={3} md={2}>
            <Checkbox
              checked={isSelected}
              inputProps={{ "aria-labelledby": labelId }}
              onClick={toggleAvailability}
              disabled={disabled}
            />
            {startCase(day)}
          </Grid>

          <Hidden smUp>
            <Grid item xs={6}>
              <Box paddingTop={1}>
                <Typography
                  align="right"
                  data-test={`working-hours-times-mobile-${day}`}
                >
                  {`${formatTime(value[0])}—${formatTime(value[1])}`}
                </Typography>
              </Box>
            </Grid>
          </Hidden>

          <Grid item xs={12} sm={7} md={8}>
            {hours && (
              <Box className={classes.slides}>
                <Slider
                  min={MIN_SLIDER_TIME}
                  max={MAX_SLIDER_TIME}
                  step={SLIDER_STEPS}
                  marks={sliderMarks}
                  value={value}
                  valueLabelFormat={formatTime}
                  valueLabelDisplay="auto"
                  onChange={(event, value) => setValue(value as number[])}
                  onChangeCommitted={(event, value) =>
                    handleSliderChange(value as number[])
                  }
                  data-test={`working-hours-slider-${day}`}
                  disabled={disabled}
                />
              </Box>
            )}
          </Grid>
          <Hidden only={"xs"}>
            <Grid item sm={2}>
              <Typography
                align="right"
                data-test={`working-hours-times-${day}`}
              >
                {`${formatTime(value[0])}—${formatTime(value[1])}`}
              </Typography>
            </Grid>
          </Hidden>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default DayHours;
